import {IDisposer, Instance, cast, types} from 'mobx-state-tree';
import {GlobalConfigDto} from '@joyrideautos/auction-core/src/dtos/GlobalConfigDto';
import BaseStore from './BaseStore';
import {reaction} from 'mobx';
import primitives from '../Primitives';

const DEFAULT_DURATION_IN_SEC = 4;
const DEFAULT_MAX_CONCURRENT_AUTOBIDS = 1;

const PayInFullConfigModel = types.model({
    passThroughFeePercent: types.maybeNull(types.number),
    remediationFeeCents: types.maybeNull(types.number),
    expirationTimeHours: types.maybeNull(types.number),
    balanceThresholdCents: types.maybe(
        types.model({
            vip: types.maybeNull(types.number),
            trusted: types.maybeNull(types.number),
            regular: types.maybeNull(types.number),
        })
    ),
});

export const GlobalConfigStore = BaseStore.named('globalConfigStore')
    .props({
        isPayDepositEnabled: primitives.boolean(false),
        shouldRefreshAfterRelease: primitives.boolean(false),
        notificationDurationSec: primitives.number(DEFAULT_DURATION_IN_SEC),
        isGateCodesEnabled: primitives.boolean(false),
        payments: types.maybe(PayInFullConfigModel),
        maxConcurrentAutobids: primitives.number(DEFAULT_MAX_CONCURRENT_AUTOBIDS),
        isMaintenanceModeEnabled: primitives.boolean(false),
        isDeprecatedModeEnabled: primitives.boolean(false),
        mp3Url: primitives.string(''),
        mp3AdminUrl: primitives.string(''),
        isMigrationModeEnabled: primitives.boolean(false),
        plannedAvailabilityTs: primitives.string(''),
    })
    .volatile(() => ({
        unsubscribeDisposer: null as null | IDisposer,
        disposers: [] as IDisposer[],
    }))
    .actions((self) => {
        return {
            updateFromGlobalConfig(globalConfig: GlobalConfigDto) {
                self.isPayDepositEnabled.setValue(globalConfig.payDepositEnabled ?? false);
                self.shouldRefreshAfterRelease.setValue(globalConfig.shouldRefreshAfterRelease ?? false);
                self.notificationDurationSec.setValue(globalConfig.notificationDurationSec ?? DEFAULT_DURATION_IN_SEC);
                self.isGateCodesEnabled.setValue(globalConfig.isGateCodesEnabled ?? false);
                self.maxConcurrentAutobids.setValue(
                    globalConfig.maxConcurrentAutobids ?? DEFAULT_MAX_CONCURRENT_AUTOBIDS
                );
                self.payments = cast(globalConfig.payments);
                self.isMaintenanceModeEnabled.setValue(globalConfig.isMaintenanceModeEnabled ?? false);
                self.isDeprecatedModeEnabled.setValue(globalConfig.isDeprecatedModeEnabled ?? false);
                self.mp3Url.setValue(globalConfig.mp3Url ?? '');
                self.mp3AdminUrl.setValue(globalConfig.mp3AdminUrl ?? '');
                self.isMigrationModeEnabled.setValue(globalConfig.isMigrationModeEnabled ?? false);
                self.plannedAvailabilityTs.setValue(globalConfig.plannedAvailabilityTs ?? '');
            },
            subscribe() {
                this.unsubscribe();
                self.unsubscribeDisposer = self.globalConfigService.subscribeToGlobalConfig(
                    (globalConfig: GlobalConfigDto) => {
                        this.updateFromGlobalConfig(globalConfig);
                    }
                );
            },
            unsubscribe() {
                self.unsubscribeDisposer?.();
                self.unsubscribeDisposer = null;
            },
            afterCreate() {
                // Subscribe immediately so maintenance/deprecation mode works for anonymous users too [MP3-2226, MP3-2227]
                this.subscribe();
                self.disposers.push(
                    reaction(
                        // A listener attached before auth (including anonymous sign-in) completes is cancelled
                        // with permission-denied and never retried by the SDK, so re-subscribe on every sign-in
                        () => self.rootStore.authUserStore.userInfo?.uid,
                        (uid) => {
                            if (uid) {
                                this.subscribe();
                            }
                        }
                    )
                );
            },
            beforeDestroy() {
                this.unsubscribe();
                self.disposers.forEach((disposer) => disposer());
            },
        };
    });

export interface GlobalConfigStoreType extends Instance<typeof GlobalConfigStore> {}

export interface HasGlobalConfigStore {
    globalConfigStore: GlobalConfigStoreType;
}
